// ─────────────────────────────────────────────
//  FolderPicker
//  Search input + scrollable folder tree.
//  Wraps FolderTreeNode with the context provider.
// ─────────────────────────────────────────────

import * as React from "react";
import {
  Input,
  ProgressBar,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Search20Regular } from "@fluentui/react-icons";
import type { FolderItem } from "../../types/sharepoint";
import { filterFolders } from "../../utils/folderUtils";
import { FolderTreeNode, FolderTreeProvider } from "./FolderTreeNode";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXXS },

  tree: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    maxHeight: "220px",
    overflowY: "auto",
    padding: `${tokens.spacingVerticalXXS} 0`,
  },

  emptyMessage: {
    color: tokens.colorNeutralForeground3,
    display: "block",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    textAlign: "center",
  },

  errorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

interface FolderPickerProps {
  folders: FolderItem[];
  loading: boolean;
  error: string;
  selectedFolderId: string;
  expandedFolders: Record<string, boolean>;
  loadingFolderIds: Record<string, boolean>;
  onSelectFolder: (node: FolderItem) => void;
  onToggleFolder: (id: string) => void;
  onExpandFolder: (node: FolderItem) => void;
}

export const FolderPicker: React.FC<FolderPickerProps> = ({
  folders,
  loading,
  error,
  selectedFolderId,
  expandedFolders,
  loadingFolderIds,
  onSelectFolder,
  onToggleFolder,
  onExpandFolder,
}) => {
  const styles = useStyles();
  const [query, setQuery] = React.useState("");

  const visible = React.useMemo(() => filterFolders(folders, query), [folders, query]);

  return (
    <div className={styles.root}>
      <Input
        placeholder="Search folders…"
        size="small"
        value={query}
        onChange={(_, d) => setQuery(d.value)}
        contentBefore={<Search20Regular fontSize={14} />}
        aria-label="Search folders"
      />

      {loading && <ProgressBar />}

      {error && (
        <Text className={styles.errorText} block>
          {error}
        </Text>
      )}

      {!loading && (
        <div className={styles.tree} role="tree" aria-label="SharePoint folders">
          {visible.length > 0 ? (
            <FolderTreeProvider
              selectedFolderId={selectedFolderId}
              expandedFolders={expandedFolders}
              loadingFolderIds={loadingFolderIds}
              onSelect={onSelectFolder}
              onToggle={onToggleFolder}
              onExpand={onExpandFolder}
            >
              {visible.map((node) => (
                <FolderTreeNode key={node.id} node={node} level={0} />
              ))}
            </FolderTreeProvider>
          ) : (
            !error && (
              <Text className={styles.emptyMessage} size={200}>
                {folders.length === 0 ? "No folders found." : "No folders match your search."}
              </Text>
            )
          )}
        </div>
      )}
    </div>
  );
};
